import { useCallback, useMemo, useRef, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { Button, Flex, ModalV2, QueryLoader } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { useQueryParam } from "~/common/hooks/useQueryParams";
import {
  useGetComplianceCaseQuery,
  useGetTaxReportingDataQuery,
} from "~/graphql/supergraphHooks";
import { BottomUpDrawer } from "~/pages/Compliance/Common/BottomUpDrawer";
import { useCurrentCaseContext } from "~/pages/Compliance/context/CurrentCaseContext";
import {
  getTaxFormConfig,
  isValidTaxFormType,
  TAX_FORM_TITLE_MAP,
  TaxFormRenderer,
  transformMetadataToTaxFormValues,
  type TaxFormValues,
} from "../../TaxForms";
import { TaxReportingPersistenceProvider } from "../../TaxForms/context/TaxReportingPersistenceContext";
import { useTaxFormSave } from "../../TaxForms/hooks/useTaxFormSave";
import { taxReportingRowToControllingPersonEntry } from "../../TaxForms/utils/taxReportingProfileSync";
import { DocumentsViewPanel } from "../DocumentManagementDisplay/DocumentsViewPanel";
import * as styles from "./styles_vanilla.css";

type TaxFormPageProps = {
  documentId: number | undefined;
};

export const TaxFormPage = ({ documentId }: TaxFormPageProps) => {
  const history = useHistory();
  const match = useRouteMatch();
  const [formTypeParam] = useQueryParam("formType");
  const [profileIdParam] = useQueryParam("profileId");
  const { caseId } = useCurrentCaseContext();
  const [showExitModal, setShowExitModal] = useState(false);
  const isDirtyRef = useRef(false);

  const formType = isValidTaxFormType(formTypeParam) ? formTypeParam : undefined;
  const profileIdFromUrl =
    profileIdParam && !Number.isNaN(Number(profileIdParam))
      ? Number(profileIdParam)
      : undefined;

  const complianceCaseData = useGetComplianceCaseQuery({
    variables: { caseId },
    skip: !caseId || caseId <= 0,
  });

  const needsTaxReportingRows = formType === "W8BENE" || formType === "CRS_ENTITY";

  const taxReportingDataResult = useGetTaxReportingDataQuery({
    variables: { caseId, documentId },
    skip: !caseId || caseId <= 0 || !documentId || !needsTaxReportingRows,
  });

  const config = useMemo(() => {
    if (!formType) return null;
    return getTaxFormConfig(formType);
  }, [formType]);

  const currentDocument = useMemo(() => {
    if (!documentId) return undefined;
    const allDocs = [
      ...(complianceCaseData.data?.complianceCase?.documents ?? []),
      ...(complianceCaseData.data?.complianceCase?.investingProfile?.documents ?? []),
    ];
    return allDocs.find((d) => d.id === documentId);
  }, [documentId, complianceCaseData.data]);

  const parentProfileId = useMemo(() => {
    return (
      profileIdFromUrl ??
      currentDocument?.profileId ??
      complianceCaseData.data?.complianceCase?.investingProfile?.id ??
      undefined
    );
  }, [
    profileIdFromUrl,
    currentDocument?.profileId,
    complianceCaseData.data?.complianceCase?.investingProfile?.id,
  ]);

  const taxReportingPersistenceValue = useMemo(() => {
    if (!needsTaxReportingRows) return null;
    if (!caseId || !documentId || parentProfileId == null || parentProfileId <= 0) return null;
    return { caseId, documentId, parentProfileId };
  }, [caseId, documentId, needsTaxReportingRows, parentProfileId]);

  const taxReportingRowsForForm = useMemo(() => {
    const rows = taxReportingDataResult.data?.taxReportingData ?? [];
    if (formType === "W8BENE") {
      return rows.filter((r) => r.taxReportingType === "fatca_substantial_owner");
    }
    if (formType === "CRS_ENTITY") {
      return rows.filter((r) => r.taxReportingType === "crs_controlling_person");
    }
    return [];
  }, [formType, taxReportingDataResult.data?.taxReportingData]);

  const initialValues = useMemo((): TaxFormValues | undefined => {
    if (!formType || !config) return undefined;
    const fromDoc = currentDocument?.metadata?.length
      ? transformMetadataToTaxFormValues(formType, currentDocument.metadata)
      : {};
    const taxFields: Partial<TaxFormValues> = {};
    if (formType === "W8BENE") {
      taxFields.fatcaSubstantialOwners = taxReportingRowsForForm.map(
        taxReportingRowToControllingPersonEntry,
      );
    } else if (formType === "CRS_ENTITY") {
      taxFields.crsControllingPersons = taxReportingRowsForForm.map(
        taxReportingRowToControllingPersonEntry,
      );
    }
    return { ...config.initialValues, ...fromDoc, ...taxFields };
  }, [config, currentDocument, formType, taxReportingRowsForForm]);

  const onCloseUrl = useMemo(() => {
    const baseUrl = match.url.replace("/tax-form", "");
    const params = new URLSearchParams();
    if (documentId) {
      params.set("documentId", String(documentId));
    }
    const queryString = params.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }, [match.url, documentId]);

  const { saveTaxForm } = useTaxFormSave({
    caseId,
    documentId,
    formType: formType ?? "W8BEN",
    profileId: profileIdFromUrl ?? currentDocument?.profileId,
    existingMetadata: currentDocument?.metadata,
    onSuccess: () => {
      history.push(onCloseUrl);
    },
  });

  const handleSubmit = useCallback(
    async (values: TaxFormValues) => {
      await saveTaxForm(values);
    },
    [saveTaxForm],
  );

  const handleBeforeClose = useCallback(() => {
    if (!isDirtyRef.current) {
      return true;
    }
    setShowExitModal(true);
    return false;
  }, []);

  const handleCancel = useCallback(
    (isDirty: boolean) => {
      if (!isDirty) {
        history.push(onCloseUrl);
        return;
      }
      setShowExitModal(true);
    },
    [history, onCloseUrl],
  );

  const handleConfirmExit = useCallback(() => {
    setShowExitModal(false);
    history.push(onCloseUrl);
  }, [history, onCloseUrl]);

  const handleCloseModal = useCallback(() => {
    setShowExitModal(false);
  }, []);

  if (!config || !formType || caseId <= 0) {
    return null;
  }

  const headerTitle = TAX_FORM_TITLE_MAP[formType] ?? config.title;

  const formPanelQueryResult =
    needsTaxReportingRows && documentId
      ? [complianceCaseData, taxReportingDataResult]
      : complianceCaseData;

  return (
    <>
      <BottomUpDrawer
        isShowing
        headerText={headerTitle}
        navigationLabel="Back to Document management"
        onCloseUrl={onCloseUrl}
        onBeforeClose={handleBeforeClose}
      >
        <div className={styles.splitContainer}>
          <div className={styles.documentPanel}>
            <DocumentsViewPanel documentId={documentId} />
          </div>
          <div className={styles.formPanelWrapper}>
            <div className={styles.formContainer}>
              <QueryLoader result={formPanelQueryResult}>
                <TaxReportingPersistenceProvider value={taxReportingPersistenceValue}>
                  <TaxFormRenderer
                    key={`${documentId ?? "na"}-${formType}`}
                    config={config}
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                    dirtyRef={isDirtyRef}
                    initialValues={initialValues}
                  />
                </TaxReportingPersistenceProvider>
              </QueryLoader>
            </div>
          </div>
        </div>
      </BottomUpDrawer>
      <ModalV2
        show={showExitModal}
        onHide={handleCloseModal}
        title="Exit and discard"
        size="small"
        isCentered
        backdropClassName={styles.exitModalBackdrop}
        contentClassName={styles.exitModalContent}
        data-testid="exit-discard-modal"
      >
        <ModalV2.Body>
          Are you sure you want to exit and discard this {headerTitle} form? This action cannot
          be undone.
        </ModalV2.Body>
        <ModalV2.Footer>
          <Flex justifyContent="flex-end" gap={Sizes.SM}>
            <Button variant="neutral" onClick={handleCloseModal} data-testid="cancel-exit-btn">
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmExit} data-testid="confirm-exit-btn">
              Exit and discard
            </Button>
          </Flex>
        </ModalV2.Footer>
      </ModalV2>
    </>
  );
};
